import { useState } from 'react'

import { Button, Field, Portal, Select } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'

import type { StageSetting } from '../types'

export interface StageDeleteDialogProps {
  stage: StageSetting
  /** Las otras columnas del mismo set: los destinos posibles. */
  others: readonly StageSetting[]
  /**
   * Clave del catálogo, con formas plurales, para contar lo que hay adentro:
   * "Esta columna tiene 4 proyectos adentro." El plural lo decide CLDR, no un
   * ternario — en portugués el 0 va en singular.
   */
  itemsKey: string
  pending?: boolean
  onCancel: () => void
  onConfirm: (moveTo: string | null) => void
}

/**
 * Borrar una columna.
 *
 * **Si tiene ítems adentro, hay que decir a dónde van** — y sólo entonces. No
 * hay opción de
 * "borrar igual": lo que quedaría es un proyecto o una tarea apuntando a una
 * columna que ya no existe, que es la clase de inconsistencia que después
 * aparece como una pantalla en blanco tres semanas más tarde.
 *
 * El destino arranca vacío a propósito: elegirlo es una decisión, no un
 * default que alguien acepta sin leer.
 */
export function StageDeleteDialog({
  stage,
  others,
  itemsKey,
  pending,
  onCancel,
  onConfirm,
}: StageDeleteDialogProps) {
  const t = useT()
  const [moveTo, setMoveTo] = useState('')
  // El destino se pide **sólo si hay algo que reubicar**. `StageRead` informa
  // `items_count`, así que la pantalla ya no tiene que asumir que la columna
  // tiene contenido: el caso mayoritario es la columna vacía.
  const needsDestination = stage.items_count > 0
  const blocked = needsDestination && moveTo === ''

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg/70 p-4">
        <div
          role="alertdialog"
          aria-modal="true"
          aria-label={t('settings.stages.deleteDialogAria', { columna: stage.name })}
          className="hairline flex w-full max-w-[460px] flex-col gap-3 rounded-[var(--radius-card)] bg-surface-1 p-4"
        >
          <h2 className="font-display text-base text-text-1">
            {t('settings.stages.deleteTitle', { columna: stage.name })}
          </h2>

          {needsDestination ? (
            <>
              {/*
                Dos oraciones enteras, no una frase cosida con el número en el
                medio: el orden de las palabras cambia en cada idioma y el
                contador ya viaja adentro de su propia entrada con plurales.
              */}
              <p className="text-xs text-text-2">
                <span className="text-text-1">{t(itemsKey, { count: stage.items_count })}</span>{' '}
                {t('settings.stages.chooseTarget')}
              </p>

              <Field label={t('settings.stages.moveTo')} htmlFor="stage-delete-move-to">
                <Select
                  id="stage-delete-move-to"
                  value={moveTo}
                  onChange={(event) => setMoveTo(event.target.value)}
                >
                  <option value="">{t('settings.stages.pickColumn')}</option>
                  {others.map((other) => (
                    <option key={other.id} value={other.id}>
                      {other.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          ) : (
            <p className="text-xs text-text-2">{t('settings.stages.deleteEmpty')}</p>
          )}

          <div className="flex justify-end gap-2">
            <Button onClick={onCancel}>{t('common.action.cancel')}</Button>
            <Button
              variant="danger"
              disabled={blocked || pending}
              onClick={() => onConfirm(moveTo === '' ? null : moveTo)}
            >
              {t('settings.stages.deleteColumn')}
            </Button>
          </div>
        </div>
      </div>
    </Portal>
  )
}
